"use client"

import { useState, useEffect } from "react"
import { Input } from "@repo/ui"
import { Eye, EyeOff, Check, X } from "lucide-react"

interface PasswordInputProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  showStrengthIndicator?: boolean
  showRequirements?: boolean
  id?: string
  name?: string
  autoComplete?: string
  required?: boolean
  disabled?: boolean
}

interface PasswordStrength {
  score: number
  feedback: string[]
  meets: {
    length: boolean
    lowercase: boolean
    uppercase: boolean
    number: boolean
    special: boolean
  }
}

export function PasswordInput({
  label = "Password",
  value,
  onChange,
  placeholder = "Enter password",
  showStrengthIndicator = false,
  showRequirements = false,
  id = "password",
  name = "password",
  autoComplete = "current-password",
  required = false,
  disabled = false
}: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [strength, setStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    meets: {
      length: false,
      lowercase: false,
      uppercase: false,
      number: false,
      special: false
    }
  })

  // Analyze password strength
  useEffect(() => {
    if (!value) {
      setStrength({
        score: 0,
        feedback: [],
        meets: {
          length: false,
          lowercase: false,
          uppercase: false,
          number: false,
          special: false
        }
      })
      return
    }

    let score = 0
    const feedback = []
    const meets = {
      length: value.length >= 8,
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /[0-9]/.test(value),
      special: /[^A-Za-z0-9]/.test(value)
    }

    // Calculate score
    if (meets.length) score++
    if (value.length >= 12) score++
    if (meets.lowercase) score++
    if (meets.uppercase) score++
    if (meets.number) score++
    if (meets.special) score++

    // Generate feedback
    if (!meets.length) feedback.push("Use at least 8 characters")
    if (!meets.lowercase) feedback.push("Include lowercase letters")
    if (!meets.uppercase) feedback.push("Include uppercase letters")
    if (!meets.number) feedback.push("Include numbers")
    if (!meets.special) feedback.push("Include special characters")

    setStrength({
      score: Math.min(score, 5),
      feedback: feedback.slice(0, 2),
      meets
    })
  }, [value])

  const getStrengthColor = () => {
    if (strength.score <= 1) return "bg-red-500"
    if (strength.score <= 2) return "bg-orange-500"
    if (strength.score <= 3) return "bg-yellow-500"
    if (strength.score <= 4) return "bg-blue-500"
    return "bg-green-500"
  }

  const getStrengthText = () => {
    if (strength.score <= 1) return "Weak"
    if (strength.score <= 2) return "Fair"
    if (strength.score <= 3) return "Good"
    if (strength.score <= 4) return "Strong"
    return "Excellent"
  }

  return (
    <div>
      <div className="relative">
        <Input
          label={label}
          type={showPassword ? "text" : "password"}
          id={id}
          name={name}
          autoComplete={autoComplete}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
        <button
          type="button"
          className="absolute right-2 top-[38px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
          tabIndex={-1}
        >
          {showPassword ? (
            <Eye className="h-5 w-5" />
          ) : (
            <EyeOff className="h-5 w-5" />
          )}
        </button>
      </div>

      {showStrengthIndicator && value && (
        <div className="mt-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Password strength:</span>
            <span className={`font-medium ${
              strength.score <= 2 ? 'text-red-600' : 
              strength.score <= 3 ? 'text-yellow-600' : 
              'text-green-600'
            }`}>
              {getStrengthText()}
            </span>
          </div>
          <div className="mt-1 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${getStrengthColor()}`}
              style={{ width: `${(strength.score / 5) * 100}%` }}
            />
          </div>
          {strength.feedback.length > 0 && (
            <p className="mt-1 text-xs text-gray-500">
              Tip: {strength.feedback.join(", ")}
            </p>
          )}
        </div>
      )}

      {showRequirements && (
        <div className="mt-3 space-y-1">
          <p className="text-xs font-medium text-gray-700 mb-1">Password requirements:</p>
          <div className="grid grid-cols-1 gap-1">
            <RequirementItem met={strength.meets.length} text="At least 8 characters" />
            <RequirementItem met={strength.meets.lowercase} text="One lowercase letter" />
            <RequirementItem met={strength.meets.uppercase} text="One uppercase letter" />
            <RequirementItem met={strength.meets.number} text="One number" />
            <RequirementItem met={strength.meets.special} text="One special character" />
          </div>
        </div>
      )}
    </div>
  )
}

function RequirementItem({ met, text }: { met: boolean; text: string }) {
  return (
    <div className="flex items-center text-xs">
      {met ? (
        <Check className="h-3 w-3 text-green-500 mr-1" />
      ) : (
        <X className="h-3 w-3 text-gray-300 mr-1" />
      )}
      <span className={met ? "text-green-700" : "text-gray-500"}>
        {text}
      </span>
    </div>
  )
}